import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

const NotificationContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const isMountedRef = useRef(true);

  // Fetch notifications directly
  const fetchNotifications = useCallback(async () => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      console.log('[Notifications] No token available');
      setError(null);
      return;
    }
    
    console.log('[Notifications] Starting fetch...');
    setLoading(true);
    setError(null);
    
    try {
      const url = `${API_URL}/api/notifications?limit=20`;
      console.log('[Notifications] Fetching:', url);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('[Notifications] Response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('[Notifications] Got', data.notifications?.length || 0, 'notifications');
        
        if (isMountedRef.current) {
          setNotifications(data.notifications || []);
          setUnreadCount(data.unread_count || 0);
          setError(null);
        }
      } else if (response.status === 401) {
        console.log('[Notifications] Unauthorized - token may be expired');
        // Don't show error for 401, just clear notifications
        if (isMountedRef.current) {
          setNotifications([]);
          setUnreadCount(0);
          setError(null);
        }
      } else {
        console.log('[Notifications] Error response:', response.status);
        if (isMountedRef.current) {
          setError(`Server error (${response.status})`);
        }
      }
    } catch (err) {
      console.error('[Notifications] Fetch failed:', err.message);
      if (isMountedRef.current) {
        setError(err.message);
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, []);

  // Mark as read
  const markAsRead = useCallback(async (ids = null) => {
    const token = localStorage.getItem('access_token');
    if (!token) return;
    
    try {
      const response = await fetch(`${API_URL}/api/notifications/mark-read`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ notification_ids: ids })
      });
      
      if (response.ok && isMountedRef.current) {
        if (ids) {
          setNotifications(prev => prev.map(n => ids.includes(n.id) ? { ...n, is_read: true } : n));
          setUnreadCount(prev => Math.max(0, prev - ids.length));
        } else {
          setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
          setUnreadCount(0);
        }
      }
    } catch (err) {
      console.error('[Notifications] Mark read failed:', err.message);
    }
  }, []);

  // Delete notification
  const deleteNotification = useCallback(async (id) => {
    const token = localStorage.getItem('access_token');
    if (!token) return;
    
    try {
      const response = await fetch(`${API_URL}/api/notifications/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok && isMountedRef.current) {
        const n = notifications.find(x => x.id === id);
        setNotifications(prev => prev.filter(x => x.id !== id));
        if (n && !n.is_read) setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (err) {
      console.error('[Notifications] Delete failed:', err.message);
    }
  }, [notifications]);

  // Clear all
  const clearAll = useCallback(async () => {
    const token = localStorage.getItem('access_token');
    if (!token) return;
    
    try {
      const response = await fetch(`${API_URL}/api/notifications`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok && isMountedRef.current) {
        setNotifications([]);
        setUnreadCount(0);
      }
    } catch (err) {
      console.error('[Notifications] Clear all failed:', err.message);
    }
  }, []);

  // Track mounted state
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Fetch on component mount
  useEffect(() => {
    const timer = setTimeout(() => {
      const token = localStorage.getItem('access_token');
      if (token) {
        fetchNotifications();
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [fetchNotifications]);

  // Poll for updates every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      const token = localStorage.getItem('access_token');
      if (token) {
        fetch(`${API_URL}/api/notifications/unread-count`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
          .then(res => res.ok ? res.json() : null)
          .then(data => {
            if (data && isMountedRef.current) {
              setUnreadCount(data.unread_count || 0);
            }
          })
          .catch(() => {});
      }
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  return (
    <NotificationContext.Provider value={{
      notifications,
      unreadCount,
      loading,
      error,
      refresh: fetchNotifications,
      markAsRead,
      deleteNotification,
      clearAll
    }}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error('useNotifications must be used within NotificationProvider');
  return ctx;
};

export default NotificationContext;
